
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import '../../styles/date-hour-minute-player.css';

export type DateLike = Moment | Date | string;

export type DateHourMinutePlayerProps = {
  today: string;
  value?: DateLike;
  onChange?: (m: Moment) => void;
  initial?: DateLike;
  isPlaying?: boolean;
  onPlayingChange?: (playing: boolean) => void;
  stepMinutes?: number;
  intervalMs?: number;
  loop?: boolean;
  size?: "sm" | "md";
  className?: string;
};

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Normalize to Moment; if invalid, use fallback. */
function toMoment(input: DateLike | undefined, fallback: Moment): Moment {
  if (input === undefined) return fallback.clone();
  if (moment.isMoment(input)) return input.clone();
  const m = typeof input === "string" ? moment(input) : moment(input);
  return m.isValid() ? m : fallback.clone();
}

/** Clamp inclusive. */
function clampMoment(m: Moment, min: Moment, max: Moment): Moment {
  if (m.isBefore(min)) return min.clone();
  if (m.isAfter(max)) return max.clone();
  return m.clone();
}

/** Safe set that clamps and blocks outside-range writes. */
function safeSet(
  next: Moment,
  min: Moment,
  max: Moment,
  write: (m: Moment) => void
) {
  if (next.isBefore(min) || next.isAfter(max)) {
    // Block: do not write anything if outside bounds.
    return;
  }
  write(next);
}

export const DateHourMinutePlayer: React.FC<DateHourMinutePlayerProps> = ({
  today,
  value,
  onChange,
  initial,
  isPlaying,
  onPlayingChange,
  stepMinutes = 10,
  intervalMs = 1000,
  loop = false,
  size = "md",
  className,
}) => {
  /** Bounds: today 00:00 … today + 14 days 23:59 */
  const min = useMemo(() => moment(today, "YYYY-MM-DD").startOf("day"), [today]);
  const max = useMemo(
    () => moment(today, "YYYY-MM-DD").add(14, "days").endOf("day"),
    [today]
  );

  /** Controlled/uncontrolled value normalized to Moment */
  const [internal, setInternal] = useState<Moment>(() => {
    const base = toMoment(initial, min);
    return clampMoment(base, min, max);
  });

  // If `value` is provided, normalize & clamp; otherwise use internal
  const m = value ? clampMoment(toMoment(value, min), min, max) : internal;

  const write = (next: Moment) => {
    const clamped = clampMoment(next, min, max);
    if (onChange) onChange(clamped);
    else setInternal(clamped);
  };

  /** Keep internal in sync if parent passes a new out-of-range `value` */
  useEffect(() => {
    if (!value) return;
    const normalized = clampMoment(toMoment(value, min), min, max);
    setInternal(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value && moment(value).valueOf(), min.valueOf(), max.valueOf()]);

  /** Play state */
  const [internalPlaying, setInternalPlaying] = useState(false);
  const playing = typeof isPlaying === "boolean" ? isPlaying : internalPlaying;
  const setPlaying = (p: boolean) => {
    if (onPlayingChange) onPlayingChange(p);
    else setInternalPlaying(p);
  };

  /** Derived display parts */
  const dayLabel = m.format("DD MMM"); // e.g., "31 Aug"
  const hourNum = Number(m.format("HH")); // 0..23
  const minuteNum = Number(m.format("mm")); // 0..59

  /** Auto-play: stop at max (or loop to min) */
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = window.setInterval(() => {
      const next = m.clone().add(stepMinutes, "minutes");
      if (next.isAfter(max)) {
        if (loop) write(min.clone());
        else setPlaying(false);
      } else {
        write(next);
      }
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, m.valueOf(), stepMinutes, intervalMs, min.valueOf(), max.valueOf(), loop]);

  /** Increment/decrement handlers (BLOCK if outside) */
  const incDay = () => safeSet(m.clone().add(1, "day"), min, max, write);
  const decDay = () => safeSet(m.clone().subtract(1, "day"), min, max, write);

  const incHour = () => safeSet(m.clone().add(1, "hour"), min, max, write);
  const decHour = () => safeSet(m.clone().subtract(1, "hour"), min, max, write);

  const incMinute = () => safeSet(m.clone().add(stepMinutes, "minutes"), min, max, write);
  const decMinute = () => safeSet(m.clone().subtract(stepMinutes, "minutes"), min, max, write);

  /** Keyboard & wheel */
  const onKeyFor =
    (kind: "day" | "hour" | "minute") => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        kind === "day" ? incDay() : kind === "hour" ? incHour() : incMinute();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        kind === "day" ? decDay() : kind === "hour" ? decHour() : decMinute();
      }
    };

  const onWheelFor =
    (kind: "day" | "hour" | "minute") => (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      const next =
        kind === "day"
          ? m.clone().add(delta, "day")
          : kind === "hour"
          ? m.clone().add(delta, "hour")
          : m.clone().add(delta * stepMinutes, "minutes");
      safeSet(next, min, max, write);
    };

  /** Disable chevrons at boundaries (visual + functional) */
  const canDecDay = m.clone().subtract(1, "day").isSameOrAfter(min);
  const canIncDay = m.clone().add(1, "day").isSameOrBefore(max);

  const canDecHour = m.clone().subtract(1, "hour").isSameOrAfter(min);
  const canIncHour = m.clone().add(1, "hour").isSameOrBefore(max);

  const canDecMinute = m.clone().subtract(stepMinutes, "minutes").isSameOrAfter(min);
  const canIncMinute = m.clone().add(stepMinutes, "minutes").isSameOrBefore(max);

  const containerClass = ["dhmp", size === "sm" ? "dhmp--sm" : "dhmp--md", className ?? ""].join(" ");

  return (
    <div className={containerClass} role="group" aria-label="Date, hour and minute selector with play/pause">
      {/* Play (left) */}
      <button
        type="button"
        className={`dhmp__control ${playing ? "is-disabled" : ""}`}
        onClick={() => setPlaying(true)}
        aria-label="Play"
        disabled={playing}
      >
        <span className="icon icon--play" aria-hidden="true" />
      </button>

      {/* Day + Month */}
      <div
        className="dhmp__column"
        tabIndex={0}
        onKeyDown={onKeyFor("day")}
        onWheel={onWheelFor("day")}
        aria-label="Day and month"
      >
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--up ${canIncDay ? "" : "is-disabled"}`}
          onClick={incDay}
          aria-label="Increase day"
          disabled={!canIncDay}
        />
        <div className="dhmp__value">{dayLabel}</div>
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--down ${canDecDay ? "" : "is-disabled"}`}
          onClick={decDay}
          aria-label="Decrease day"
          disabled={!canDecDay}
        />
      </div>

      {/* Hour */}
      <div
        className="dhmp__column"
        tabIndex={0}
        onKeyDown={onKeyFor("hour")}
        onWheel={onWheelFor("hour")}
        aria-label="Hour"
      >
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--up ${canIncHour ? "" : "is-disabled"}`}
          onClick={incHour}
          aria-label="Increase hour"
          disabled={!canIncHour}
        />
        <div className="dhmp__value">{pad2(hourNum)}</div>
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--down ${canDecHour ? "" : "is-disabled"}`}
          onClick={decHour}
          aria-label="Decrease hour"
          disabled={!canDecHour}
        />
      </div>

      {/* Colon */}
      <div className="dhmp__separator" aria-hidden="true">:</div>

      {/* Minute */}
      <div
        className="dhmp__column"
        tabIndex={0}
        onKeyDown={onKeyFor("minute")}
        onWheel={onWheelFor("minute")}
        aria-label="Minute"
      >
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--up ${canIncMinute ? "" : "is-disabled"}`}
          onClick={incMinute}
          aria-label="Increase minute"
          disabled={!canIncMinute}
        />
        <div className="dhmp__value">{pad2(minuteNum)}</div>
        <button
          type="button"
          className={`dhmp__chevron dhmp__chevron--down ${canDecMinute ? "" : "is-disabled"}`}
          onClick={decMinute}
          aria-label="Decrease minute"
          disabled={!canDecMinute}
        />
      </div>

      {/* Pause (right) */}
      <button
        type="button"
        className={`dhmp__control ${playing ? "" : "is-disabled"}`}
        onClick={() => setPlaying(false)}
        aria-label="Pause"
        disabled={!playing}
      >
        <span className="icon icon--pause" aria-hidden="true" />
      </button>
    </div>
  );
};
